import { createDescriptor, DescriptorUpdate, uploadPhotoToPresignedUrl } from '../clients/photo-upload-client';
import { updateTeam } from '../clients/team-upload';
import { Years, Grades, TeamNumbers, TeamResults, DataUtils } from '../clients/data-service';
import React from "react";
import prettyBytes from 'pretty-bytes';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    Grid,
    GridItem, Heading, Image,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Spacer,
    Spinner,
    Stack,
    Text,
    Textarea
} from '@chakra-ui/react';
import { ImageSubjectEntry } from './ImageSubjectEntry';
import { PeopleUpdater } from './PeopleUpdater';
import { DraftImageSubject, ImageSubjectUtilities } from '../utilities/ImageSubjectUtilities';
import { AnnotateImage } from './AnnotateImage';

export type PhotoUploadProps = {
    image: File;
    isTeamPhoto: boolean;
    cancelImage: (boolean) => void;
};

export type PhotoUploadState = {
    dataUrl: string | null;
    form: ImageDetails;
    uploadingInProgress: boolean;
    imageSubjects: DraftImageSubject[]
};

type ImageDetails = {
    photoName: string;
    photoDesc: string;
    year: string;
    grade: string;
    teamNo: string;
    result: string;
    wereMinorPremiers: boolean;
};


export class PhotoUpload extends React.Component<PhotoUploadProps, PhotoUploadState> {
    peopleUpdater: PeopleUpdater;

    constructor(props: PhotoUploadProps) {
        super(props);
        let initialName = "";
        this.state = {
            dataUrl: null,
            form: {
                photoName: initialName,
                photoDesc: "",
                year: "",
                grade: "",
                teamNo: "0",
                result: "",
                wereMinorPremiers: false
            },
            uploadingInProgress: false,
            imageSubjects: AnnotateImage.startingEmptySubjects(2, 4)
        };
        this.loadDataUrl(props.image, this);
        this.upload = this.upload.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        
        const puRef = this;
        const peopleAct = (action) => {
            puRef.setState(prev => {
                const people = structuredClone(prev.imageSubjects);
                action(people);
                return {imageSubjects: people};
            });
        }
        const peopleActInPlace = (action) => {
            puRef.setState(prev => {
                action(prev.imageSubjects);
                return prev;
            });
        }
        this.peopleUpdater = new PeopleUpdater(peopleAct, peopleActInPlace);
    }

    loadDataUrl(image, self) {
        let reader = new FileReader();
        reader.onload = (e) => {
            if (e?.target?.result == null || e.target.result instanceof ArrayBuffer) {
                return;
            }
            console.log('isJpeg: ', e.target.result.includes('data:image/jpeg'));
            console.log('length: ', e.target.result.length);
            if (!e.target.result.includes('data:image/jpeg')) {
                return alert('Wrong file type - JPG only.');
            }
            //   if (e.target.result.length > MAX_IMAGE_SIZE) {
            //     return alert('Image is loo large.')
            //   }
            self.setState({ dataUrl: e.target.result });
        };
        reader.readAsDataURL(image);
    }

    handleInputChange(event) {
        const target = event.target;
        let value = target.type === 'checkbox' ? target.checked : target.value;
        if (value == null) {
            value = "";
        }
        const formField = target.name;

        this.setState((prev) => {
            const clone = structuredClone(prev.form);
            clone[formField] = value;
            return { form: clone };
        });
    }

    async upload() {
        console.log(JSON.stringify(this.state.form));

        //await this.dummyUpload();
        await this.realUpload();
    }

    private async realUpload() {
        this.setState({ uploadingInProgress: true });
        try {
            const form = this.state.form;
            form.photoName = DataUtils.TeamName({year: parseInt(form.year, 10), grade: form.grade, team_number: form.teamNo}) + " Team Photo";
            console.log(form);
            const teamId = await updateTeam(form.year, form.grade, form.teamNo, {result: form.result, minor_premiers: form.wereMinorPremiers});

            const descriptorCreationDetails: DescriptorUpdate = {
                subjects: ImageSubjectUtilities.draftToSubmittableSubjects(this.state.imageSubjects),
                name: form.photoName,
                caption: form.photoDesc,
                metadata: form,
                teamId: teamId,
                imageType: "TeamPhoto"
            };

            const createDescriptorResult = await createDescriptor(descriptorCreationDetails);
            await updateTeam(form.year, form.grade, form.teamNo, {team_image_id: createDescriptorResult.descriptor_id});
            console.log(createDescriptorResult);
            const uploadResult = await uploadPhotoToPresignedUrl(createDescriptorResult.upload_url, this.props.image);
            console.log(uploadResult);
            this.setState({ uploadingInProgress: false });
            if (uploadResult.status === 200) {
                console.log("success");
                this.props.cancelImage(true);
            }
        } catch (e) {
            // TODO: Show error
            console.log(e);
            this.setState({ uploadingInProgress: false });
        }
    }

    private async dummyUpload() {
        this.setState({ uploadingInProgress: true });
        await new Promise(res => setTimeout(res, 15000));
        this.setState({ uploadingInProgress: false });
        this.props.cancelImage(true);
    }

    render() {
        const imageDetails = this.props.image;

        const yearInvalid = this.state.form.year == "";
        const gradeInvalid = this.state.form.grade == "";
        const teamNoInvalid = this.state.form.teamNo == "";

        const formIncomplete = yearInvalid || gradeInvalid || teamNoInvalid;

        const maxContentHeight = Math.min(600, window.innerHeight * 0.6);
        

        return (
            <Box>
                <Grid templateColumns="repeat(5, 1fr)" gap={4}>
                    <GridItem colSpan={3}>
                        <Box>
                            <Heading size='md'>Photo</Heading>
                            {this.state.dataUrl !== null && <Image src={this.state.dataUrl} maxH={maxContentHeight}></Image>}
                        </Box>
                        <Stack>
                            <Text>Name: {imageDetails.name}, Size: {prettyBytes(imageDetails.size)}</Text>
                        </Stack>
                    </GridItem>
                    <GridItem colSpan={2}>
                        <Stack spacing={3}>
                            {!this.props.isTeamPhoto && (<>
                                <Heading size='md'>Image Details</Heading>
                                <Input type="text" placeholder="Photo Name" name="photoName" value={this.state.form.photoName} onChange={this.handleInputChange} />
                                <Textarea placeholder="Photo Caption" name="photoDesc" value={this.state.form.photoDesc} onChange={this.handleInputChange} />
                                <Divider p={2} />
                            </>)}

                            <Heading size='md'>Team {this.props.isTeamPhoto && "(Required)"}</Heading>
                            <FormControl isInvalid={yearInvalid}>
                                <Select name="year" placeholder="Year" value={this.state.form.year} onChange={this.handleInputChange}>
                                    {Years().map(year => <option key={year} value={year}>{year}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isInvalid={gradeInvalid}>
                            <Select name="grade" placeholder="Grade" value={this.state.form.grade} onChange={this.handleInputChange}>
                                {Grades().map(grade => <option value={grade.Key}>{grade.Selection}</option>)}
                            </Select>
                            </FormControl>
                            <FormControl isInvalid={teamNoInvalid}>
                            <Select name="teamNo" placeholder="Team No." value={this.state.form.teamNo} onChange={this.handleInputChange}>
                                {TeamNumbers().map(grade => <option value={grade.Key}>{grade.Selection}</option>)}
                            </Select>
                            </FormControl>
                            <Divider p={2} />

                            <Heading size='md'>Team Results (Optional)</Heading>
                            <Select name="result" placeholder="Result" value={this.state.form.result} onChange={this.handleInputChange}>
                                {TeamResults().map(grade => <option value={grade.Key}>{grade.Selection}</option>)}
                            </Select>
                            <Checkbox type="checkbox" name="wereMinorPremiers" checked={this.state.form.wereMinorPremiers} onChange={this.handleInputChange}>
                                Minor Premiers
                            </Checkbox>
                        </Stack>
                    </GridItem>
                    <GridItem colSpan={5}>
                        <Divider height="5px" alignSelf="stretch" objectFit="cover" position="relative" size="small" orientation="horizontal" marginBottom={2}/>
                        <Heading size='md'>Label People (Optional)</Heading>
                        <ImageSubjectEntry people={this.state.imageSubjects} peopleUpdater={this.peopleUpdater}/>
                    </GridItem>
                    <GridItem colSpan={5}>
                        <Flex w='100%'  alignItems='right' gap='2'>
                            <Spacer/>
                            <Button colorScheme='red' w={250} onClick={() => this.props.cancelImage(false)}>Cancel</Button>
                            <Button colorScheme='green' w={250} isDisabled={formIncomplete} onClick={this.upload}>Upload</Button>
                        </Flex>
                    </GridItem>
                    
                </Grid>
                <Modal isOpen={this.state.uploadingInProgress} onClose={() => { }} isCentered>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader textAlign='center'>
                            Photo Uploading
                        </ModalHeader>
                        <ModalBody>
                            <Center p={4}>
                                <Spinner />
                            </Center>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </Box>
        );
    }
}
